import React from "react";
import { Button } from "@/race-tracker/components/ui/button";
import { RefreshCw, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/lib/AuthContext";
import { base44, fetchRaceTrackerSweepConfig, patchRaceTrackerSweepConfigSerperLocale } from "@/race-tracker/api/rtClient";
import { normalizeSerperMode, normalizeSerperQueryFields } from "@/race-tracker/lib/serperQueryCatalog";
import SynthesisCard from "./SynthesisCard";
import GoogleSection from "./GoogleSection";
import FECSection from "./FECSection";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/race-tracker/components/ui/table";
import { Badge } from "@/race-tracker/components/ui/badge";

const PROVIDER_LABEL = { anthropic: "Claude", openai: "ChatGPT", gemini: "Gemini" };

function modelForProvider(cfg, provider) {
  if (!cfg || !provider) return "—";
  if (provider === "openai") return cfg.openai_model || "—";
  if (provider === "gemini") return cfg.gemini_model || "—";
  return cfg.anthropic_model || "—";
}

function formatConfiguredSynthesis(cfg) {
  if (!cfg) return null;
  const primary = cfg.primary_llm_provider || "anthropic";
  const fb = cfg.fallback_llm_provider;
  const parts = [
    `Primary ${PROVIDER_LABEL[primary] || primary} (${modelForProvider(cfg, primary)})`,
  ];
  if (fb) {
    parts.push(
      `fallback ${PROVIDER_LABEL[fb] || fb} (${modelForProvider(cfg, fb)})`,
    );
  }
  return parts.join("; ");
}

function formatRunLlm(run) {
  const p = run.synthesis_provider;
  const m = run.synthesis_model;
  if (!p && !m) return "—";
  if (!p) return m || "—";
  return `${PROVIDER_LABEL[p] || p}${m ? ` · ${m}` : ""}`;
}

export default function SweepResults({ race, advertiser, opponents, sweepResult, isSweeping, onRunSweep }) {
  const queryClient = useQueryClient();
  const { isSuperAdmin } = useAuth();
  const raceLabel = `${race.state} ${race.chamber.replace("State ", "").replace("U.S. ", "")} ${race.district || ""}`.trim();
  const advertiserName = (advertiser && advertiser.advertiser_name) || "";
  const oppNames = opponents.map((o) => o.opponent_name).join(", ") || "No opponents configured";

  const saveSerperRaceQuery = async ({ serper_query_fields, serper_mode }) => {
    await base44.entities.Race.update(race.id, {
      serper_query_fields: normalizeSerperQueryFields(serper_query_fields),
      serper_mode: normalizeSerperMode(serper_mode),
    });
    await queryClient.invalidateQueries({ queryKey: ["races"] });
  };

  const saveSerperLocale = async (body) => {
    await patchRaceTrackerSweepConfigSerperLocale(body);
    await queryClient.invalidateQueries({ queryKey: ["raceTrackerSweepConfig"] });
  };

  const skippedList = Array.isArray(sweepResult?.skipped_sources) ? sweepResult.skipped_sources : [];

  const { data: sweepConfig, isLoading: configLoading } = useQuery({
    queryKey: ["raceTrackerSweepConfig"],
    queryFn: fetchRaceTrackerSweepConfig,
    staleTime: 5 * 60 * 1000,
  });

  const { data: sweepRunsData, isLoading: runsLoading } = useQuery({
    queryKey: ["sweepRuns", race.id],
    queryFn: () => base44.entities.SweepRun.listByRace(race.id, { limit: 100 }),
    enabled: !!race?.id,
  });

  const runs = sweepRunsData?.rows ?? [];
  const runCount = sweepRunsData?.count ?? 0;
  const latestRun = runs[0] || null;

  const configLine = formatConfiguredSynthesis(sweepConfig);

  const synthesisMetaLine =
    latestRun && (latestRun.synthesis_provider || latestRun.synthesis_model)
      ? `Latest logged briefing: ${formatRunLlm(latestRun)}${latestRun.used_fallback ? " (fallback)" : ""}`
      : null;

  const historyBlock = (
    <div className="rounded-lg border border-border bg-card/30 p-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Sweep history</p>
        {!runsLoading && (
          <span className="text-[10px] text-muted-foreground tabular-nums">{runCount} total run{runCount !== 1 ? "s" : ""}</span>
        )}
      </div>
      {runsLoading ? (
        <p className="text-xs text-muted-foreground flex items-center gap-2">
          <Loader2 className="w-3 h-3 animate-spin" /> Loading…
        </p>
      ) : runs.length === 0 ? (
        <p className="text-[11px] text-muted-foreground">No runs logged yet for this race.</p>
      ) : (
        <div className="rounded-md border border-border overflow-x-auto max-h-[220px] overflow-y-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="text-[10px] whitespace-nowrap">When</TableHead>
                <TableHead className="text-[10px] whitespace-nowrap">User</TableHead>
                <TableHead className="text-[10px] whitespace-nowrap">Duration</TableHead>
                <TableHead className="text-[10px] whitespace-nowrap">Serper</TableHead>
                <TableHead className="text-[10px] whitespace-nowrap">FEC</TableHead>
                <TableHead className="text-[10px] whitespace-nowrap">LLM</TableHead>
                <TableHead className="text-[10px] whitespace-nowrap"> </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {runs.map((run) => (
                <TableRow key={run.id}>
                  <TableCell className="text-[10px] whitespace-nowrap tabular-nums">
                    {run.created_at
                      ? format(new Date(run.created_at), "MMM d, yyyy h:mm a")
                      : "—"}
                  </TableCell>
                  <TableCell className="text-[10px] max-w-[140px] truncate" title={run.user_email || ""}>
                    {run.user_email || "—"}
                  </TableCell>
                  <TableCell className="text-[10px] tabular-nums">
                    {run.sweep_duration_seconds != null ? `${Number(run.sweep_duration_seconds).toFixed(1)}s` : "—"}
                  </TableCell>
                  <TableCell className="text-[10px] tabular-nums">{run.serper_hits ?? "—"}</TableCell>
                  <TableCell className="text-[10px] tabular-nums">{run.fec_hits ?? "—"}</TableCell>
                  <TableCell className="text-[10px] max-w-[160px] truncate" title={formatRunLlm(run)}>
                    {formatRunLlm(run)}
                  </TableCell>
                  <TableCell className="text-[10px]">
                    {run.used_fallback ? (
                      <Badge variant="secondary" className="text-[9px] px-1 py-0 h-5">Fallback</Badge>
                    ) : (
                      ""
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );

  const modelStrip = (
    <div className="rounded-md border border-border bg-muted/20 px-3 py-2 space-y-1">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Synthesis routing</p>
      {configLoading ? (
        <p className="text-xs text-muted-foreground flex items-center gap-2">
          <Loader2 className="w-3 h-3 animate-spin" /> Loading settings…
        </p>
      ) : configLine ? (
        <p className="text-[11px] text-foreground leading-snug">{configLine}</p>
      ) : (
        <p className="text-[11px] text-muted-foreground">Could not load synthesis settings.</p>
      )}
      {synthesisMetaLine && (
        <p className="text-[10px] text-muted-foreground border-t border-border/60 pt-1.5 mt-1">{synthesisMetaLine}</p>
      )}
    </div>
  );

  if (!sweepResult && !isSweeping) {
    return (
      <div className="flex flex-col h-full overflow-y-auto">
        <div className="flex flex-col items-center justify-center text-center p-8 space-y-4 flex-1 min-h-[280px]">
          <p className="text-lg font-bold">{raceLabel}</p>
          <p className="text-sm text-muted-foreground">{oppNames}</p>
          <p className="text-xs text-muted-foreground">This race has never been swept.</p>
          <Button onClick={onRunSweep} className="gap-2">
            <RefreshCw className="w-4 h-4" /> Run First Sweep
          </Button>
        </div>
        <div className="p-5 pt-0 space-y-4 border-t border-border">
          {modelStrip}
          {historyBlock}
        </div>
      </div>
    );
  }

  return (
    <div className="p-5 space-y-5">
      {/* Race Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold font-mono">{raceLabel}</h2>
          <p className="text-xs text-muted-foreground mt-0.5">{oppNames}</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {sweepResult
              ? `Last swept ${format(new Date(sweepResult.sweep_timestamp), "MMMM d, yyyy 'at' h:mm a")}`
              : "Never swept"}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="text-xs gap-1.5 shrink-0"
          onClick={onRunSweep}
          disabled={isSweeping}
        >
          {isSweeping ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          {isSweeping ? "Sweeping…" : "Re-run Sweep"}
        </Button>
      </div>

      {modelStrip}

      {/* Synthesis */}
      <SynthesisCard
        synthesis={sweepResult?.synthesis_text}
        synthesisTimestamp={sweepResult?.synthesis_timestamp}
        isSweeping={isSweeping}
        serperResults={sweepResult?.serper_results || []}
        serperSkipped={skippedList.find((s) => /serper/i.test(s)) || ""}
      />

      {/* Source Sections */}
      <GoogleSection
        serperResults={sweepResult?.serper_results || []}
        isSweeping={isSweeping}
        serperSkipped={skippedList.find((s) => /serper/i.test(s))}
        settingsHref="/admin/integrations"
        race={race}
        raceLabel={raceLabel}
        advertiserName={advertiserName}
        opponents={opponents}
        sweepConfig={sweepConfig}
        sweepConfigLoading={configLoading}
        isSuperAdmin={isSuperAdmin}
        onSaveSerperRaceQuery={saveSerperRaceQuery}
        onSaveSerperLocale={saveSerperLocale}
      />
      <FECSection
        results={sweepResult?.fec_results || []}
        isSweeping={isSweeping}
        settingsHref="/admin/integrations"
      />

      {/* Footer */}
      {sweepResult && (
        <div className="text-[10px] text-muted-foreground border-t border-border pt-3 space-y-0.5">
          <p>Sources queried: Serper, FEC API</p>
          {sweepResult.sweep_duration_seconds != null && (
            <p>Sweep duration: {Number(sweepResult.sweep_duration_seconds).toFixed(1)}s</p>
          )}
          {skippedList.map((s, i) => (
            <p key={i} className="text-amber-600">Skipped: {s}</p>
          ))}
        </div>
      )}

      {historyBlock}
    </div>
  );
}
